"use client";

import Image from "next/image";
import { useState } from "react";

type PriceItem = {
  id: string;
  model: string;
  description: string;
  originalPrice: number;
  vendor: string;
};

type SmartBulkPreview = {
  rawInput: string;
  qty: number;
  matchedItem?: PriceItem;
  confidencePercent: number;
  status: "Matched" | "Needs Confirmation" | "Discount" | "No Match";
  discountVendor?: string;
  discountPercent?: number;
  confirmed?: boolean;
};

export default function Home() {
  const [query, setQuery] = useState("");
  const [discountRate, setDiscountRate] = useState(0);
  const [items, setItems] = useState<PriceItem[]>([]);
  const [selectedMap, setSelectedMap] = useState<Record<string, PriceItem>>({});
  const [qtyMap, setQtyMap] = useState<Record<string, string>>({});
  const [discountMap, setDiscountMap] = useState<Record<string, string>>({});
  const [vendorDiscountMap, setVendorDiscountMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedModel, setCopiedModel] = useState<string | null>(null);
  const [copiedBulk, setCopiedBulk] = useState(false);
  const [copiedSummary, setCopiedSummary] = useState(false);
  const [viewMode, setViewMode] = useState<"desktop" | "phone">("desktop");
  const [smartBulkInput, setSmartBulkInput] = useState("");
  const [smartBulkPreview, setSmartBulkPreview] = useState<SmartBulkPreview[]>([]);
  const [smartBulkLoading, setSmartBulkLoading] = useState(false);

  function itemKey(item: PriceItem): string {
    return item.id;
  }

  function getQuantity(key: string): number {
    const n = Number.parseInt(qtyMap[key] ?? "1", 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  function normalizeVendor(value: string): string {
    return value.replace(/[^A-Z]/gi, "").toUpperCase();
  }

  function vendorMatches(itemVendor: string, commandVendor: string): boolean {
    const normalizedItemVendor = normalizeVendor(itemVendor);
    const normalizedCommandVendor = normalizeVendor(commandVendor);
    return normalizedItemVendor.includes(normalizedCommandVendor);
  }

  function normalizeModel(value: string): string {
    return value.replace(/[\s\-_]/g, "").toUpperCase();
  }

  function parseSmartBulkLines(input: string): { rawInput: string; model: string; qty: number }[] {
    return input
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(/\s+/);
        const last = parts[parts.length - 1];
        const qty = /^\d+$/.test(last) ? Math.max(1, Number.parseInt(last, 10)) : 1;
        const model = /^\d+$/.test(last) ? parts.slice(0, -1).join("") : line;
        return { rawInput: line, model, qty };
      });
  }

  function parseDiscountLine(model: string): { vendor: string; percent: number } | null {
    const trimmed = model.trim();
    const percentMatch = trimmed.match(/(?:^|\s)A[-_ ]?(\d{1,3}(?:\.\d+)?)$/i);
    if (!percentMatch) return null;

    const percent = Number.parseFloat(percentMatch[1]);
    if (!Number.isFinite(percent)) return null;

    const vendor = trimmed
      .replace(/(?:^|\s)A[-_ ]?\d{1,3}(?:\.\d+)?$/i, "")
      .trim();
    if (!vendor) return null;

    return {
      vendor: vendor.toUpperCase(),
      percent: Math.min(100, Math.max(0, percent)),
    };
  }

  function isDiscountLine(model: string): boolean {
    return parseDiscountLine(model) !== null;
  }

  function fuzzyScore(input: string, candidate: string): number {
    let matches = 0;
    let j = 0;
    for (let i = 0; i < input.length; i += 1) {
      while (j < candidate.length && candidate[j] !== input[i]) j += 1;
      if (j < candidate.length) {
        matches += 1;
        j += 1;
      }
    }
    return input.length ? matches / input.length : 0;
  }

  function matchSmartBulkModel(model: string, allItems: PriceItem[]): SmartBulkPreview {
    const normalized = normalizeModel(model);
    const discountLine = parseDiscountLine(model);

    if (discountLine) {
      return {
        rawInput: model,
        qty: 1,
        confidencePercent: 100,
        status: "Discount",
        discountVendor: discountLine.vendor,
        discountPercent: discountLine.percent,
      };
    }

    const exact = allItems.find((item) => normalizeModel(item.model) === normalized);
    if (exact) return { rawInput: model, qty: 1, matchedItem: exact, confidencePercent: 100, status: "Matched" };

    const prefix = allItems.find((item) => normalizeModel(item.model).startsWith(normalized));
    if (prefix) return { rawInput: model, qty: 1, matchedItem: prefix, confidencePercent: 92, status: "Matched" };

    const contains = allItems.find((item) => normalizeModel(item.model).includes(normalized));
    if (contains) {
      return { rawInput: model, qty: 1, matchedItem: contains, confidencePercent: 78, status: normalized.length >= 6 ? "Matched" : "Needs Confirmation" };
    }

    const fuzzyCandidates = allItems
      .map((item) => ({ item, score: fuzzyScore(normalized, normalizeModel(item.model)) }))
      .sort((a, b) => b.score - a.score);
    if (fuzzyCandidates[0] && fuzzyCandidates[0].score >= 0.8) {
      return {
        rawInput: model,
        qty: 1,
        matchedItem: fuzzyCandidates[0].item,
        confidencePercent: Math.round(fuzzyCandidates[0].score * 100),
        status: "Needs Confirmation",
      };
    }

    return { rawInput: model, qty: 1, confidencePercent: 0, status: "No Match" };
  }

  async function runSmartBulkMatch() {
    setSmartBulkLoading(true);
    setError(null);
    try {
      const parsed = parseSmartBulkLines(smartBulkInput);
      const res = await fetch("/api/prices?all=1");
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to load product database for smart bulk input");
        return;
      }
      const allItems = (data.items || []) as PriceItem[];
      const preview = parsed.map((entry) => {
        const matched = matchSmartBulkModel(entry.model, allItems);
        return { ...matched, rawInput: entry.rawInput, qty: entry.qty };
      });
      setSmartBulkPreview(preview);
    } catch {
      setError("Failed to process smart bulk input");
    } finally {
      setSmartBulkLoading(false);
    }
  }

  function toggleSmartBulkConfirmation(index: number) {
    setSmartBulkPreview((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              confirmed: !row.confirmed,
            }
          : row,
      ),
    );
  }

  function addSmartBulkMatchedRows() {
    const matched = smartBulkPreview.filter(
      (row) =>
        row.matchedItem &&
        (row.status === "Matched" ||
          (row.status === "Needs Confirmation" && row.confirmed)),
    );
    const discounts = smartBulkPreview.filter((row) => row.status === "Discount" && row.discountVendor);
    if (matched.length === 0 && discounts.length === 0) return;

    const matchedItems = matched.map((row) => row.matchedItem!) as PriceItem[];
    setItems((prev) => {
      const existing = new Set(prev.map((item) => item.id));
      const additions = matchedItems.filter((item) => !existing.has(item.id));
      return [...additions, ...prev];
    });
    setSelectedMap((prev) => {
      const next = { ...prev };
      for (const row of matched) next[row.matchedItem!.id] = row.matchedItem!;
      return next;
    });
    setQtyMap((prev) => {
      const next = { ...prev };
      for (const row of matched) next[row.matchedItem!.id] = String(row.qty || 1);
      return next;
    });
    const nextVendorDiscounts: Record<string, string> = {};
    for (const row of discounts) {
      nextVendorDiscounts[row.discountVendor!] = String(row.discountPercent ?? 0);
    }

    setVendorDiscountMap((prev) => ({ ...prev, ...nextVendorDiscounts }));
    setDiscountMap((prev) => {
      const next = { ...prev };
      for (const row of matched) {
        const item = row.matchedItem!;
        const matchedVendorCommand = Object.keys(nextVendorDiscounts).find((vendor) =>
          vendorMatches(item.vendor || "", vendor),
        );
        if (matchedVendorCommand) {
          next[item.id] = nextVendorDiscounts[matchedVendorCommand];
        }
      }
      return next;
    });
  }

  async function search() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/prices?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Search failed");
        setItems([]);
        return;
      }
      setItems(data.items || []);
    } catch {
      setError("Failed to fetch prices");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  function roundToNearestTen(value: number): number {
    return Math.round(value / 10) * 10;
  }

  function getRowDiscountRate(key: string): number {
    const rowItem = items.find((item) => itemKey(item) === key) || selectedMap[key];
    const matchedVendorKey = rowItem
      ? Object.keys(vendorDiscountMap).find((vendor) =>
          vendorMatches(rowItem.vendor || "", vendor),
        )
      : undefined;
    const vendorRaw = matchedVendorKey ? vendorDiscountMap[matchedVendorKey] : undefined;
    const raw = discountMap[key];

    if (raw !== undefined && raw !== "") {
      const n = Number.parseFloat(raw);
      if (Number.isFinite(n)) return Math.min(100, Math.max(0, n));
    }

    if (vendorRaw !== undefined && vendorRaw !== "") {
      const n = Number.parseFloat(vendorRaw);
      if (Number.isFinite(n)) return Math.min(100, Math.max(0, n));
    }

    return discountRate;
  }

  function getDisplayedDiscountValue(key: string): string {
    const rowItem = items.find((item) => itemKey(item) === key) || selectedMap[key];
    const matchedVendorKey = rowItem
      ? Object.keys(vendorDiscountMap).find((vendor) =>
          vendorMatches(rowItem.vendor || "", vendor),
        )
      : undefined;

    if (discountMap[key] !== undefined) return discountMap[key];
    if (matchedVendorKey && vendorDiscountMap[matchedVendorKey] !== undefined) {
      return vendorDiscountMap[matchedVendorKey];
    }
    return String(discountRate);
  }

  function getDiscountedUnitPrice(item: PriceItem): number {
    const key = itemKey(item);
    const rowDiscount = getRowDiscountRate(key);
    const factor = Math.max(0, 1 - rowDiscount / 100);
    return roundToNearestTen(item.originalPrice * factor);
  }

  function quoteLine(item: PriceItem): string {
    const key = itemKey(item);
    const qty = getQuantity(key);
    const discounted = getDiscountedUnitPrice(item);
    const total = discounted * qty;
    return `${item.model} | Discounted Price HKD$${discounted.toLocaleString()} | Qty ${qty} | Total HKD$${total.toLocaleString()} | ${item.description}`;
  }

  function toggleSelect(item: PriceItem) {
    const key = itemKey(item);
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = item;
      return next;
    });
  }

  function toggleSelectAll() {
    const currentKeys = items.map(itemKey);
    const allCurrentSelected =
      currentKeys.length > 0 && currentKeys.every((k) => Boolean(selectedMap[k]));

    setSelectedMap((prev) => {
      const next = { ...prev };
      if (allCurrentSelected) {
        for (const k of currentKeys) delete next[k];
      } else {
        for (const item of items) next[itemKey(item)] = item;
      }
      return next;
    });
  }

  function onQtyChange(key: string, value: string) {
    setQtyMap((prev) => ({ ...prev, [key]: value }));
  }

  function onDiscountChange(key: string, value: string) {
    setDiscountMap((prev) => ({ ...prev, [key]: value }));
  }

  async function copyLine(item: PriceItem) {
    await navigator.clipboard.writeText(quoteLine(item));
    setCopiedModel(item.model);
    setTimeout(() => setCopiedModel(null), 1200);
  }

  async function copySelected() {
    const selectedItems = Object.values(selectedMap);
    if (selectedItems.length === 0) return;

    const details = selectedItems.map(quoteLine).join("\n\n");
    const totalQty = selectedItems.reduce(
      (sum, item) => sum + getQuantity(itemKey(item)),
      0,
    );
    const grandTotal = selectedItems.reduce(
      (sum, item) => sum + getDiscountedUnitPrice(item) * getQuantity(itemKey(item)),
      0,
    );

    const summary = [
      "----- SUMMARY -----",
      `Selected Models: ${selectedItems.length}`,
      `Total Qty: ${totalQty}`,
      `Grand Total: HKD$${grandTotal.toLocaleString()}`,
    ].join("\n");

    await navigator.clipboard.writeText(`${details}\n\n${summary}`);
    setCopiedBulk(true);
    setTimeout(() => setCopiedBulk(false), 1200);
  }

  async function copySelectedWithSummary() {
    const selectedItems = Object.values(selectedMap);
    if (selectedItems.length === 0) return;

    const details = selectedItems.map(quoteLine).join("\n\n");
    const totalQty = selectedItems.reduce(
      (sum, item) => sum + getQuantity(itemKey(item)),
      0,
    );
    const grandTotal = selectedItems.reduce(
      (sum, item) => sum + getDiscountedUnitPrice(item) * getQuantity(itemKey(item)),
      0,
    );

    const summary = [
      "----- SUMMARY -----",
      `Selected Models: ${selectedItems.length}`,
      `Total Qty: ${totalQty}`,
      `Grand Total: HKD$${grandTotal.toLocaleString()}`,
    ].join("\n");

    await navigator.clipboard.writeText(`${details}\n\n${summary}`);
    setCopiedSummary(true);
    setTimeout(() => setCopiedSummary(false), 1200);
  }

  function clearSelected() {
    setSelectedMap({});
  }

  function clearSmartBulkAndSelected() {
    setSmartBulkInput("");
    setSmartBulkPreview([]);
    setSelectedMap({});
    setQtyMap({});
    setDiscountMap({});
    setVendorDiscountMap({});
  }

  const selectedItems = Object.values(selectedMap);
  const selectedCount = selectedItems.length;
  const selectedTotalQty = selectedItems.reduce(
    (sum, item) => sum + getQuantity(itemKey(item)),
    0,
  );
  const selectedOriginalTotal = selectedItems.reduce(
    (sum, item) => sum + item.originalPrice * getQuantity(itemKey(item)),
    0,
  );
  const selectedGrandTotal = selectedItems.reduce(
    (sum, item) => sum + getDiscountedUnitPrice(item) * getQuantity(itemKey(item)),
    0,
  );
  const selectedSavings = selectedOriginalTotal - selectedGrandTotal;
  const allSelected =
    items.length > 0 && items.every((item) => Boolean(selectedMap[itemKey(item)]));

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 p-4 text-slate-900 md:p-8">
      <div className="mx-auto max-w-6xl rounded-3xl border border-white/50 bg-white/85 p-6 shadow-xl backdrop-blur md:p-8">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 md:flex-row md:items-end md:justify-between">
          <div className="flex items-center gap-4">
            <Image
              src="/sanyik-logo.png"
              alt="San Yik Air Conditioning Engineering Co., Ltd."
              width={220}
              height={66}
              priority
              className="h-auto w-[180px] rounded-md border border-slate-200 bg-white p-1 md:w-[220px]"
            />
            <div>
              <h2 className="text-base font-bold text-slate-800 md:text-lg">
                San Yik Air Conditioning Engineering Co., Ltd.
              </h2>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">
                Daikin AC Price Search
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Search model, apply discount %, and copy one-line quote instantly.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1 text-xs font-semibold">
              <button
                onClick={() => setViewMode("desktop")}
                className={`rounded px-2 py-1 ${viewMode === "desktop" ? "bg-slate-900 text-white" : "text-slate-700"}`}
              >
                Desktop
              </button>
              <button
                onClick={() => setViewMode("phone")}
                className={`rounded px-2 py-1 ${viewMode === "phone" ? "bg-slate-900 text-white" : "text-slate-700"}`}
              >
                Phone
              </button>
            </div>
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              <span>Results</span>
              <span className="rounded-full bg-white px-2 py-0.5 text-slate-900">
                {items.length}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-[1fr_180px_120px]">
          <input
            className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            placeholder="Search model or description (e.g. RUXYQ10)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
          <input
            type="number"
            min={0}
            max={100}
            step={0.1}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            placeholder="Discount %"
            value={discountRate}
            onChange={(e) => setDiscountRate(Number(e.target.value || 0))}
          />
          <button
            onClick={search}
            className="rounded-xl bg-gradient-to-r from-slate-900 to-blue-900 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-90"
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </div>

        <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">Smart Bulk Model Input</h3>
          <p className="mt-1 text-xs text-slate-500">
            Paste messy model lines here. Format: model + optional qty. Discount lines like DIS A-60 / DIT A-50 will apply vendor discounts.
          </p>
          <textarea
            value={smartBulkInput}
            onChange={(e) => setSmartBulkInput(e.target.value)}
            placeholder={"RXYQ10BB 2\nFXSP200DA 10\nDIS A-60"}
            className="mt-3 min-h-28 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={runSmartBulkMatch}
              className="rounded-lg bg-gradient-to-r from-slate-900 to-blue-900 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:scale-[1.01] hover:opacity-90 active:scale-[0.98]"
            >
              {smartBulkLoading ? "Matching..." : "Smart Match Input"}
            </button>
            <button
              onClick={addSmartBulkMatchedRows}
              disabled={!smartBulkPreview.some((row) => row.matchedItem && (row.status === "Matched" || (row.status === "Needs Confirmation" && row.confirmed)))}
              className="rounded-lg bg-gradient-to-r from-blue-700 to-indigo-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:scale-[1.01] hover:opacity-90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add Matched Rows
            </button>
            <button
              onClick={clearSmartBulkAndSelected}
              className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700 shadow-sm transition hover:scale-[1.01] hover:bg-rose-100 active:scale-[0.98]"
            >
              Clear Bulk Input + Selected
            </button>
          </div>
          {smartBulkPreview.length > 0 && (
            <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Raw Input</th>
                    <th className="px-3 py-2 text-left">Qty</th>
                    <th className="px-3 py-2 text-left">Matched Model</th>
                    <th className="px-3 py-2 text-left">Confidence</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {smartBulkPreview.map((row, index) => (
                    <tr key={`${row.rawInput}-${index}`} className="border-t border-slate-100">
                      <td className="px-3 py-2">{row.rawInput}</td>
                      <td className="px-3 py-2">{row.qty}</td>
                      <td className="px-3 py-2">{row.matchedItem?.model || (row.status === "Discount" ? `${row.discountVendor} vendor discount ${row.discountPercent}%` : "-")}</td>
                      <td className="px-3 py-2">{row.confidencePercent}%</td>
                      <td className="px-3 py-2">{row.status}{row.status === "Needs Confirmation" && row.confirmed ? " (Confirmed)" : ""}</td>
                      <td className="px-3 py-2">
                        {row.status === "Needs Confirmation" && row.matchedItem ? (
                          <button
                            onClick={() => toggleSmartBulkConfirmation(index)}
                            className={`rounded px-2 py-1 text-xs font-medium ${row.confirmed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                          >
                            {row.confirmed ? "Confirmed" : "Confirm Match"}
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="mt-3 text-xs text-slate-500">
          Global discount is the default. You can override discount % per row.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={search}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium shadow-sm transition hover:bg-slate-100"
          >
            Refresh Results
          </button>
          <button
            onClick={toggleSelectAll}
            disabled={items.length === 0}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {allSelected ? "Unselect All" : "Select All"}
          </button>
          <button
            onClick={clearSelected}
            disabled={selectedCount === 0}
            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear Selected
          </button>
          <button
            onClick={copySelected}
            disabled={selectedCount === 0}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {copiedBulk
              ? `Copied ${selectedCount} row${selectedCount > 1 ? "s" : ""}`
              : `Copy Selected (${selectedCount})`}
          </button>
          <button
            onClick={copySelectedWithSummary}
            disabled={selectedCount === 0}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {copiedSummary ? "Copied with Summary" : "Copy Selected + Grand Total"}
          </button>
        </div>

        <p className="mt-2 text-xs text-slate-600">
          Selected: {selectedCount} model{selectedCount > 1 ? "s" : ""} | Total Qty: {selectedTotalQty} | Grand Total: HKD${selectedGrandTotal.toLocaleString()}
        </p>

        {selectedCount > 0 && (
          <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900">Pinned Selected Items</h3>
              <span className="text-xs text-slate-500">Always kept while you continue searching</span>
            </div>

            <div className="mt-3 grid gap-2 md:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
                <div className="text-slate-500">Original Subtotal</div>
                <div className="text-base font-bold text-slate-800">HKD${selectedOriginalTotal.toLocaleString()}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
                <div className="text-slate-500">Discount Savings</div>
                <div className="text-base font-bold text-emerald-700">HKD${selectedSavings.toLocaleString()}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-blue-50 p-3 text-xs">
                <div className="text-slate-500">Grand Total</div>
                <div className="text-base font-bold text-blue-800">HKD${selectedGrandTotal.toLocaleString()}</div>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              {selectedItems.map((item) => {
                const key = itemKey(item);
                const qty = getQuantity(key);
                const discounted = getDiscountedUnitPrice(item);
                const total = discounted * qty;
                return (
                  <div key={`pin-${key}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2 text-xs">
                    <div className="min-w-[150px] flex-1 font-semibold text-slate-800">{item.model}</div>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={getDisplayedDiscountValue(key)}
                      onChange={(e) => onDiscountChange(key, e.target.value)}
                      className="w-20 rounded border border-slate-300 px-2 py-1"
                    />
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={qtyMap[key] ?? "1"}
                      onChange={(e) => onQtyChange(key, e.target.value)}
                      className="w-16 rounded border border-slate-300 px-2 py-1"
                    />
                    <div className="min-w-[120px] font-semibold text-emerald-700">HKD${total.toLocaleString()}</div>
                    <button
                      onClick={() => toggleSelect(item)}
                      className="rounded border border-rose-300 bg-rose-50 px-2 py-1 text-rose-700 hover:bg-rose-100"
                    >
                      Unpin
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {error && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        {viewMode === "desktop" ? (
          <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200 text-left text-slate-700">
                  <th className="px-4 py-3 font-semibold">Select</th>
                  <th className="px-4 py-3 font-semibold">Model</th>
                  <th className="px-4 py-3 font-semibold">Description</th>
                  <th className="px-4 py-3 font-semibold">Original Price</th>
                  <th className="px-4 py-3 font-semibold">Discount %</th>
                  <th className="px-4 py-3 font-semibold">Discounted Price</th>
                  <th className="px-4 py-3 font-semibold">Qty</th>
                  <th className="px-4 py-3 font-semibold">Total</th>
                  <th className="px-4 py-3 font-semibold">Vendor</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const key = itemKey(item);
                  const selected = Boolean(selectedMap[key]);
                  const discounted = getDiscountedUnitPrice(item);
                  const qty = getQuantity(key);
                  const total = discounted * qty;

                  return (
                    <tr
                      key={key}
                      className="border-b border-slate-100 align-top transition hover:bg-blue-50/40"
                    >
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected} onChange={() => toggleSelect(item)} className="h-4 w-4 cursor-pointer rounded border-slate-300" />
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{item.model}</td>
                      <td className="px-4 py-3 text-slate-700">{item.description}</td>
                      <td className="px-4 py-3 text-slate-700">{item.originalPrice.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <input type="number" min={0} max={100} step={0.1} value={getDisplayedDiscountValue(key)} onChange={(e) => onDiscountChange(key, e.target.value)} className="w-24 rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                      </td>
                      <td className="px-4 py-3 font-semibold text-emerald-700">{discounted.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <input type="number" min={1} step={1} value={qtyMap[key] ?? "1"} onChange={(e) => onQtyChange(key, e.target.value)} className="w-20 rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-800">HKD${total.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">{item.vendor}</span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => copyLine(item)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium shadow-sm transition hover:bg-slate-100">
                          {copiedModel === item.model ? "Copied" : "Copy"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-6 grid gap-3">
            {items.map((item) => {
              const key = itemKey(item);
              const selected = Boolean(selectedMap[key]);
              const discounted = getDiscountedUnitPrice(item);
              const qty = getQuantity(key);
              const total = discounted * qty;

              return (
                <div key={key} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-bold text-slate-900">{item.model}</div>
                      <div className="text-xs text-slate-600">{item.description}</div>
                    </div>
                    <input type="checkbox" checked={selected} onChange={() => toggleSelect(item)} className="mt-1 h-4 w-4" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-slate-500">Original</div>
                      <div className="font-semibold">{item.originalPrice.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Vendor</div>
                      <div className="font-semibold">{item.vendor}</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Discount %</div>
                      <input type="number" min={0} max={100} step={0.1} value={getDisplayedDiscountValue(key)} onChange={(e) => onDiscountChange(key, e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <div className="text-slate-500">Qty</div>
                      <input type="number" min={1} step={1} value={qtyMap[key] ?? "1"} onChange={(e) => onQtyChange(key, e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <div className="text-slate-500">Discounted</div>
                      <div className="font-semibold text-emerald-700">HKD${discounted.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Total</div>
                      <div className="font-semibold">HKD${total.toLocaleString()}</div>
                    </div>
                  </div>
                  <button onClick={() => copyLine(item)} className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium shadow-sm transition hover:bg-slate-100">
                    {copiedModel === item.model ? "Copied" : "Copy"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {!loading && items.length === 0 && (
          <p className="mt-3 text-sm text-slate-500">
            No results yet. Search a model to begin.
          </p>
        )}

        <footer className="mt-6 border-t border-slate-200 pt-4 text-center text-xs text-slate-500">
          <div>Created by Jarvis and Kevin</div>
          <div>Cortexops System Limited</div>
          <div className="mt-3 flex justify-center">
            <Image
              src="/cortexops-logo.png"
              alt="CortexOps"
              width={220}
              height={220}
              className="h-auto w-[140px] rounded-md border border-slate-200 bg-white p-1"
            />
          </div>
        </footer>
      </div>
    </main>
  );
}
